/**
 * The route-name vocabulary of the event modal's ticketing section: the tabs, in the
 * order the tab bar shows them, plus the one view under them that is not a tab.
 *
 * Shared by `EventDetailsRoutes` (which turns each entry into a route record) and
 * `useTicketingTabs` (which turns the enabled ones into tabs), so a rename cannot
 * leave the tab bar pointing at a route name that no longer exists.
 *
 * Deliberately dependency-free: the router imports it, and so does a composable
 * that reads the account store, so anything imported here would end up in the
 * router's module graph.
 */
/**
 * Route name after the context prefix — `Calendar.` / `Events.` / `Modal.`.
 *
 * A union rather than `string` so that `useTicketingTabs`' label map has to cover
 * every suffix: adding a tab here without labelling it, or renaming one and
 * forgetting the label, is then a type error rather than a blank tab.
 */
export type TicketingTabSuffix = 'Event.Tickets' | 'Event.Addons' | 'Event.Orders' | 'Event.Discounts' | 'Event.Presales';

export type TicketingTabKey = 'tickets' | 'addons' | 'orders' | 'discounts' | 'presales';

export type TicketingTabDefinition = {
  /** Path segment under `:eventId`. */
  segment: TicketingTabKey;
  suffix: TicketingTabSuffix;
  /** Account feature that has to be on for the tab to be offered. */
  feature: string;
};

export const TICKETS_TAB_SUFFIX: TicketingTabSuffix = 'Event.Tickets';

export const DISCOUNTS_TAB_SUFFIX: TicketingTabSuffix = 'Event.Discounts';

// The event rail's item is labelled "Tickets" too: that is the section, not this tab.
// Add-ons share the Tickets flag because an add-on is a ticket blueprint of kind `addon`.
export const TICKETING_TABS: TicketingTabDefinition[] = [
  { segment: 'tickets', suffix: TICKETS_TAB_SUFFIX, feature: 'tickets_spa' },
  { segment: 'addons', suffix: 'Event.Addons', feature: 'tickets_spa' },
  { segment: 'orders', suffix: 'Event.Orders', feature: 'orders_spa' },
  { segment: 'discounts', suffix: DISCOUNTS_TAB_SUFFIX, feature: 'discounts_spa' },
  { segment: 'presales', suffix: 'Event.Presales', feature: 'presales_spa' },
];

/**
 * The order details panel under the Orders tab — `:eventId/orders/:orderId`.
 *
 * Not a tab: no `TICKETING_TABS` entry, no `useTicketingTabs` label, and deliberately
 * outside `TicketingTabSuffix`. It lives here anyway because three places have to agree
 * on the name — the route record, the row's link and the panel's back arrow — and
 * `Event.Order` differs from `Event.Orders` by one character.
 */
export const ORDER_DETAIL_SUFFIX = 'Event.Order';

/** The create panel under the Discounts tab — not a tab either, and named in three places. */
export const DISCOUNT_CREATE_SUFFIX = 'Event.Discount.New';

/** The create panel under the Presales tab — not a tab either, and named in three places. */
export const PRESALE_CREATE_SUFFIX = 'Event.Presale.New';

/** The create panel under the Tickets tab — not a tab either, and named in three places. */
export const TICKET_CREATE_SUFFIX = 'Event.Ticket.New';

/** The create panel under the Add-ons tab — not a tab either, and named in three places. */
export const ADDON_CREATE_SUFFIX = 'Event.Addon.New';

/** The edit panel under the Tickets tab — not a tab either, and named in three places. */
export const TICKET_EDIT_SUFFIX = 'Event.Ticket.Edit';

/** The panel converting one of a ticket's holds into a ticket — not a tab either, and named in three places. */
export const TICKET_CONVERT_SUFFIX = 'Event.Ticket.Convert';

/** The create panel for a guest list, opened from the Tickets tab's More menu — not a tab either. */
export const GUEST_LIST_CREATE_SUFFIX = 'Event.GuestList.New';

/** The ticket counts report opened from the Tickets tab's More menu — not a tab either. */
export const TICKET_COUNTS_REPORT_SUFFIX = 'Event.Ticket.CountsReport';

/** The seating chart sidebar opened from the Tickets tab's More menu — not a tab either. */
export const TICKET_SEATING_CHARTS_SUFFIX = 'Event.Ticket.SeatingCharts';

/** The edit panel under the Add-ons tab — not a tab either, and named in three places. */
export const ADDON_EDIT_SUFFIX = 'Event.Addon.Edit';
